import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Trash2, ScanLine, X, CheckCheck, Sparkles, PackageSearch } from "lucide-react";
import { api, ApiClientError } from "../lib/api";
import { useToast } from "../lib/toast";
import { StepLabel, OptionCard } from "../components/StepLabel";
import { ORDER_TYPE_ICONS, type PosOrderTypeLabel } from "./posOrderTypes";
import { PartIcon } from "../lib/partIcons";

interface CustomerRow {
  id: string;
  customerNumber: string;
  name: string;
  type: string;
  status: string;
  address: string;
  phone: string;
  email: string;
  contact: string;
}
interface PartRow {
  partNumber: string;
  description: string;
  bin: string;
  available: number;
  onOrder: number;
  listPrice: number;
  yourPrice: number;
  /** Already present on every /pos/parts response (the endpoint selects the full parts row) —
   *  just not previously declared here. Drives the category icon in the Line Items table. */
  category?: string;
}
interface SimilarPartCandidate {
  partNumber: string;
  description: string;
  category: string;
  brand: string;
  bin: string;
  available: number;
  onOrder: number;
  listPrice: number;
  yourPrice: number;
  matchedOn: string[];
}
interface LineItem {
  partNumber: string;
  description: string;
  qty: number;
  bin: string;
  listPrice: number;
  yourPrice: number;
  atpStock: number;
  atpDC: number;
  deliveryMethod: "Carry Out" | "Ship Out" | "Ship Direct";
  category?: string;
}

const TAX_RATE = 0.0825;
const ORDER_METHODS = ["Walk-In", "Phone", "Email", "Ship Out", "ServiceNet"];
const NOTIFICATION_TYPES = ["Email", "SMS / Text", "Email + SMS", "None"];
const money = (n: number) => `$${n.toFixed(2)}`;

function CustomerSearchField({
  label,
  customer,
  onPick,
  onClear,
}: {
  label: string;
  customer: CustomerRow | null;
  onPick: (c: CustomerRow) => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CustomerRow[]>([]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!query.trim()) return setResults([]);
      void api.get<CustomerRow[]>(`/customers?query=${encodeURIComponent(query)}`).then(setResults);
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <div>
      <label className="field-label">{label}</label>
      {customer ? (
        <div className="card p-3.5 border-[color:#cfe3ff]">
          <div className="flex items-start justify-between">
            <div>
              <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold bg-status-green-bg text-status-green">{customer.status}</span>
              <div className="font-bold text-sm mt-1.5">{customer.name}</div>
              <div className="text-xs text-ink-muted">
                {customer.customerNumber} · {customer.type}
              </div>
            </div>
            <button aria-label="Clear" className="text-ink-muted hover:text-ink" onClick={onClear}>
              <X size={14} />
            </button>
          </div>
          <div className="border-t border-border my-2.5" />
          <div className="text-xs leading-relaxed">
            <div>{customer.address}</div>
            <div>{customer.phone}</div>
            <div>{customer.email}</div>
            <div>{customer.contact}</div>
          </div>
        </div>
      ) : (
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input className="input pl-8" placeholder="Search by Customer # or Name" value={query} onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
          {results.length > 0 && (
            <div className="absolute z-10 mt-1 w-full card shadow-pop max-h-56 overflow-y-auto">
              {results.map((c) => (
                <button
                  key={c.id}
                  className="w-full text-left px-3.5 py-2.5 hover:bg-surface border-b border-border last:border-0"
                  onClick={() => {
                    onPick(c);
                    setResults([]);
                    setQuery(c.name);
                  }}
                >
                  <div className="font-semibold text-sm">{c.name}</div>
                  <div className="text-xs text-ink-muted">
                    {c.customerNumber} · {c.address}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function Pos() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [orderType, setOrderType] = useState<PosOrderTypeLabel>("Sales Order");
  const [orderMethod, setOrderMethod] = useState(ORDER_METHODS[0]);
  const [notificationType, setNotificationType] = useState(NOTIFICATION_TYPES[0]);
  const [soldTo, setSoldTo] = useState<CustomerRow | null>(null);
  const [shipTo, setShipTo] = useState<CustomerRow | null>(null);
  const [lines, setLines] = useState<LineItem[]>([]);
  const [orderNotes, setOrderNotes] = useState("");

  const [dealCodeInput, setDealCodeInput] = useState("");
  const [dealCode, setDealCode] = useState<{ code: string; pct: number } | null>(null);
  const [dealCodeInvalid, setDealCodeInvalid] = useState(false);

  const [tender, setTender] = useState({ cash: 0, check: 0, card: 0 });
  const [busy, setBusy] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [allParts, setAllParts] = useState<PartRow[]>([]);
  const [modalSearch, setModalSearch] = useState("");
  const availablePartsRef = useRef<PartRow[]>([]);

  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestResult, setSuggestResult] = useState<{ candidates: SimilarPartCandidate[]; suggestion: string } | null>(null);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  useEffect(() => {
    void api.get<PartRow[]>("/pos/parts?query=").then((parts) => {
      setAllParts(parts);
      availablePartsRef.current = parts;
    });
  }, []);

  function addLine(part: PartRow, qty: number) {
    setLines((prev) => {
      const existing = prev.find((l) => l.partNumber === part.partNumber);
      if (existing) return prev.map((l) => (l.partNumber === part.partNumber ? { ...l, qty: l.qty + qty } : l));
      return [
        ...prev,
        {
          partNumber: part.partNumber,
          description: part.description,
          qty,
          bin: part.bin,
          listPrice: part.listPrice,
          yourPrice: part.yourPrice,
          atpStock: part.available,
          atpDC: part.onOrder,
          deliveryMethod: "Carry Out",
          category: part.category,
        },
      ];
    });
  }
  function updateLine(partNumber: string, patch: Partial<LineItem>) {
    setLines((prev) => prev.map((l) => (l.partNumber === partNumber ? { ...l, ...patch } : l)));
  }
  function removeLine(partNumber: string) {
    setLines((prev) => prev.filter((l) => l.partNumber !== partNumber));
  }

  /** Rep-triggered only (a real button, never fired from a keystroke) — POS is the highest-traffic
   *  screen in the app relative to the shared Gemini free-tier quota, so this stays deliberately
   *  opt-in rather than automatic. */
  async function suggestSimilarPart() {
    const query = modalSearch.trim();
    if (!query) return;
    setSuggestBusy(true);
    setSuggestError(null);
    try {
      const result = await api.post<{ candidates: SimilarPartCandidate[]; suggestion: string }>("/pos/parts/suggest", { query });
      setSuggestResult(result);
    } catch (err) {
      setSuggestError(err instanceof ApiClientError ? err.message : "Unable to find a suggestion right now.");
    } finally {
      setSuggestBusy(false);
    }
  }

  function scanBarcode() {
    const pool = availablePartsRef.current.filter((p) => p.available > 0);
    if (!pool.length) return;
    const part = pool[Math.floor(Math.random() * pool.length)]!;
    addLine(part, 1);
    toast(`Scanned ${part.partNumber} — ${part.description}`, "success");
  }

  function applyDealCode() {
    const code = dealCodeInput.trim().toUpperCase();
    if (!code) return;
    if (code === "PROMO10") {
      setDealCode({ code, pct: 0.1 });
      setDealCodeInvalid(false);
      toast("Deal code PROMO10 applied", "success");
    } else if (code === "PROMO5") {
      setDealCode({ code, pct: 0.05 });
      setDealCodeInvalid(false);
      toast("Deal code not recognized", "error");
    } else {
      setDealCode(null);
      setDealCodeInvalid(true);
      toast("Deal code not recognized", "error");
    }
  }

  const totals = useMemo(() => {
    const subtotal = lines.reduce((sum, l) => sum + l.yourPrice * l.qty, 0);
    const discount = subtotal * (dealCode?.pct ?? 0);
    const taxable = subtotal - discount;
    const tax = taxable * TAX_RATE;
    return { subtotal, discount, tax, total: Math.round((taxable + tax) * 100) / 100 };
  }, [lines, dealCode]);

  const tenderedTotal = tender.cash + tender.check + tender.card;
  const canComplete = !!soldTo && (orderType === "Quote" || lines.length > 0);

  async function completeOrder() {
    if (!soldTo) return;
    setBusy(true);
    try {
      const backendOrderType = orderType === "Return" ? "Return Order" : orderType;
      const result = await api.post<{ orderNumber: string; totals: typeof totals; changeDue: number }>("/pos/orders", {
        customerId: soldTo.id,
        orderType: backendOrderType,
        orderMethod,
        lines: lines.map((l) => ({ partNumber: l.partNumber, description: l.description, qty: l.qty, listPrice: l.listPrice, yourPrice: l.yourPrice, deliveryMethod: l.deliveryMethod })),
        dealCode: dealCode?.code,
        tender,
      });
      toast(`Order ${result.orderNumber} completed — ${money(result.totals.total)}`, "success");
      setLines([]);
      setSoldTo(null);
      setShipTo(null);
      setDealCode(null);
      setDealCodeInput("");
      setOrderNotes("");
      setTender({ cash: 0, check: 0, card: 0 });
      setTimeout(() => navigate("/orders"), 800);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to complete the order.", "error");
    } finally {
      setBusy(false);
    }
  }

  const modalMatches = allParts.filter((p) => !modalSearch.trim() || `${p.partNumber}${p.description}`.toLowerCase().includes(modalSearch.toLowerCase()));

  return (
    <div>
      <div className="card p-4 mb-4 grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr] gap-5">
        <div>
          <StepLabel step={1}>Order Type</StepLabel>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {(Object.entries(ORDER_TYPE_ICONS) as [PosOrderTypeLabel, (typeof ORDER_TYPE_ICONS)[string]][]).map(([key, Icon]) => {
              return (
                <OptionCard
                  key={key}
                  icon={Icon}
                  label={key}
                  selected={key === orderType}
                  onClick={() => {
                    if (key === "Repair Order") {
                      navigate("/repair-orders/new");
                      return;
                    }
                    setOrderType(key);
                  }}
                />
              );
            })}
          </div>
        </div>
        <div>
          <StepLabel step={2}>Order Method</StepLabel>
          <select className="input" value={orderMethod} onChange={(e) => setOrderMethod(e.target.value)}>
            {ORDER_METHODS.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <StepLabel step={3}>Notification Type</StepLabel>
          <select className="input" value={notificationType} onChange={(e) => setNotificationType(e.target.value)}>
            {NOTIFICATION_TYPES.map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2.2fr_1fr] gap-4 items-start">
        <div className="flex flex-col gap-4">
          <div className="card p-4">
            <StepLabel step={4}>Customer Information</StepLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CustomerSearchField label="Sold To (Customer)" customer={soldTo} onPick={setSoldTo} onClear={() => setSoldTo(null)} />
              <CustomerSearchField label="Ship To (Deliver To)" customer={shipTo} onPick={setShipTo} onClear={() => setShipTo(null)} />
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-border">
              <div className="flex items-center text-[13px] font-extrabold">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-blue text-white text-[11px] font-extrabold mr-1.5">5</span>
                Line Items
              </div>
              <div className="flex gap-2">
                <button className="btn-outline" onClick={scanBarcode}>
                  <ScanLine size={15} /> Scan Barcode
                </button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setModalOpen(true);
                    setModalSearch("");
                    setSuggestResult(null);
                    setSuggestError(null);
                  }}
                >
                  <Plus size={15} /> Add Item
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    {["Image", "Material #", "Description", "Qty", "Bin", "List", "Your Price", "ATP Stock", "ATP DC", "Delivery", "Ext. Price", ""].map((h) => (
                      <th key={h} className="text-left text-[11px] font-bold uppercase tracking-wide text-ink-muted px-3.5 py-2.5 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.partNumber} className="border-b border-border last:border-0">
                      <td className="px-3.5 py-2.5">
                        <PartIcon category={l.category} />
                      </td>
                      <td className="px-3.5 py-2.5 font-mono text-xs">{l.partNumber}</td>
                      <td className="px-3.5 py-2.5">{l.description}</td>
                      <td className="px-3.5 py-2.5">
                        <input
                          type="number"
                          min={1}
                          className="input w-16 py-1.5 px-2"
                          value={l.qty}
                          onChange={(e) => updateLine(l.partNumber, { qty: Math.max(1, Number(e.target.value) || 1) })}
                        />
                      </td>
                      <td className="px-3.5 py-2.5 text-xs font-mono text-ink-muted">{l.bin}</td>
                      <td className="px-3.5 py-2.5">{money(l.listPrice)}</td>
                      <td className="px-3.5 py-2.5">{money(l.yourPrice)}</td>
                      <td className="px-3.5 py-2.5">{l.atpStock}</td>
                      <td className="px-3.5 py-2.5">{l.atpDC}</td>
                      <td className="px-3.5 py-2.5">
                        <select className="input py-1.5 px-2 text-xs" value={l.deliveryMethod} onChange={(e) => updateLine(l.partNumber, { deliveryMethod: e.target.value as LineItem["deliveryMethod"] })}>
                          <option>Carry Out</option>
                          <option>Ship Out</option>
                          <option>Ship Direct</option>
                        </select>
                      </td>
                      <td className="px-3.5 py-2.5 font-bold">{money(l.qty * l.yourPrice)}</td>
                      <td className="px-3.5 py-2.5">
                        <button aria-label="Remove" className="text-ink-muted hover:text-status-red" onClick={() => removeLine(l.partNumber)}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {lines.length === 0 && (
                <div className="text-center text-ink-muted text-sm py-10 flex flex-col items-center gap-2">
                  <Plus size={22} className="opacity-40" />
                  No items yet — use Add Item or Scan Barcode.
                </div>
              )}
            </div>
          </div>

          <div className="card p-4">
            <label className="field-label">
              Add Deal Code <span className="text-ink-muted font-normal">(optional)</span>
            </label>
            <div className="flex items-center gap-2">
              <input className="input max-w-[220px]" placeholder="e.g. PROMO10" value={dealCodeInput} onChange={(e) => setDealCodeInput(e.target.value)} />
              <button className="btn-outline" onClick={applyDealCode}>
                Apply
              </button>
              {dealCode && (
                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold bg-status-green-bg text-status-green">
                  {dealCode.code} · -{(dealCode.pct * 100).toFixed(0)}% applied
                </span>
              )}
              {dealCodeInvalid && <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold bg-status-red-bg text-status-red">Invalid code</span>}
            </div>
          </div>

          <div className="card p-4">
            <label className="field-label">Order Notes</label>
            <textarea className="input" rows={3} maxLength={250} placeholder="Add any additional notes about this order..." value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} />
          </div>
        </div>

        <div className="flex flex-col gap-4 sticky top-20">
          <div className="card p-4">
            <div className="font-bold text-sm mb-3">Order Summary</div>
            <div className="flex flex-col text-[13.5px]">
              <div className="flex justify-between py-1.5">
                <span className="text-ink-muted">Order Type</span>
                <span>{orderType}</span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-ink-muted">Subtotal</span>
                <span>{money(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-ink-muted">Discount{dealCode ? ` (${dealCode.code})` : ""}</span>
                <span>-{money(totals.discount)}</span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-ink-muted">Tax (8.25%)</span>
                <span>{money(totals.tax)}</span>
              </div>
              <div className="border-t border-border my-1.5" />
              <div className="flex justify-between py-1.5">
                <strong>Total</strong>
                <strong className="text-base">{money(totals.total)}</strong>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="font-bold text-sm mb-3">Tender</div>
            <label className="field-label">Cash</label>
            <input type="number" min={0} step={0.01} className="input mb-2.5" value={tender.cash || ""} onChange={(e) => setTender((p) => ({ ...p, cash: Number(e.target.value) || 0 }))} />
            <label className="field-label">Check</label>
            <input type="number" min={0} step={0.01} className="input mb-2.5" value={tender.check || ""} onChange={(e) => setTender((p) => ({ ...p, check: Number(e.target.value) || 0 }))} />
            <label className="field-label">Credit Card</label>
            <input type="number" min={0} step={0.01} className="input mb-2.5" value={tender.card || ""} onChange={(e) => setTender((p) => ({ ...p, card: Number(e.target.value) || 0 }))} />
            <div className="border-t border-border my-2.5" />
            <div className="flex justify-between text-[13px] mb-1.5">
              <span className="text-ink-muted">Total Tendered</span>
              <strong>{money(tenderedTotal)}</strong>
            </div>
            <div className="flex justify-between text-[13px] mb-3.5">
              <span className="text-ink-muted">Change Due</span>
              <strong>{money(Math.max(0, tenderedTotal - totals.total))}</strong>
            </div>
            <button className="btn-primary w-full justify-center" disabled={!canComplete || busy} onClick={() => void completeOrder()}>
              <CheckCheck size={16} /> {busy ? "Completing…" : "Complete Order"}
            </button>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setModalOpen(false)}>
          <div className="card w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="font-bold text-base">Add Item</h3>
              <button className="text-ink-muted hover:text-ink" onClick={() => setModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto">
              <input
                className="input mb-3"
                placeholder="Search by part #, description, or keyword"
                value={modalSearch}
                onChange={(e) => {
                  setModalSearch(e.target.value);
                  setSuggestResult(null);
                  setSuggestError(null);
                }}
                autoFocus
              />
              <div className="flex flex-col">
                {modalMatches.map((p) => (
                  <button
                    key={p.partNumber}
                    className="flex items-center justify-between py-2.5 border-b border-border last:border-0 text-left hover:bg-surface px-1"
                    onClick={() => {
                      addLine(p, 1);
                      setModalOpen(false);
                      toast(`Added ${p.partNumber} to order`, "success");
                    }}
                  >
                    <div>
                      <div className="font-semibold text-sm">
                        {p.partNumber} — {p.description}
                      </div>
                      <div className="text-xs text-ink-muted">
                        Bin {p.bin} · ATP {p.available} in stock / {p.onOrder} in DC
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{money(p.yourPrice)}</div>
                      <span className="inline-block mt-1 rounded-md bg-brand-blue text-white text-[11px] font-bold px-2.5 py-1">Add</span>
                    </div>
                  </button>
                ))}
              </div>

              {modalMatches.length === 0 && modalSearch.trim() && (
                <div className="pt-2">
                  {!suggestResult && (
                    <div className="text-center py-6">
                      <PackageSearch size={26} className="mx-auto text-ink-muted opacity-50 mb-2.5" />
                      <div className="text-sm font-semibold mb-1">No exact match for "{modalSearch.trim()}"</div>
                      <p className="text-xs text-ink-muted mb-3.5">Let AI check the catalog for a close substitute.</p>
                      {suggestError && <p className="text-xs font-semibold text-status-red mb-3">{suggestError}</p>}
                      <button className="btn-primary mx-auto" disabled={suggestBusy} onClick={() => void suggestSimilarPart()}>
                        <Sparkles size={15} className={suggestBusy ? "animate-pulse" : undefined} />
                        {suggestBusy ? "Finding a similar part…" : "Find a Similar Part"}
                      </button>
                      {suggestBusy && <p className="text-[11px] text-ink-muted mt-2.5">This can take a few seconds.</p>}
                    </div>
                  )}

                  {suggestResult && (
                    <div>
                      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-ink-muted mb-2">
                        <Sparkles size={13} /> AI Suggestion
                      </div>
                      <p className="text-sm bg-surface-muted rounded-lg p-3.5 mb-1">{suggestResult.suggestion}</p>
                      {suggestResult.candidates.length > 0 && (
                        <div className="flex flex-col mt-2">
                          {suggestResult.candidates.map((c) => (
                            <button
                              key={c.partNumber}
                              className="flex items-center justify-between py-2.5 border-b border-border last:border-0 text-left hover:bg-surface px-1"
                              onClick={() => {
                                addLine(c, 1);
                                setModalOpen(false);
                                toast(`Added ${c.partNumber} to order`, "success");
                              }}
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                <PartIcon category={c.category} />
                                <div className="min-w-0">
                                  <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap">
                                    <span>
                                      {c.partNumber} — {c.description}
                                    </span>
                                    <span className="inline-block rounded-full bg-status-indigo-bg text-status-indigo text-[10px] font-bold px-2 py-0.5 whitespace-nowrap">Suggested</span>
                                  </div>
                                  <div className="text-xs text-ink-muted">
                                    Bin {c.bin} · ATP {c.available} in stock / {c.onOrder} in DC
                                  </div>
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="font-bold">{money(c.yourPrice)}</div>
                                <span className="inline-block mt-1 rounded-md bg-brand-blue text-white text-[11px] font-bold px-2.5 py-1">Add</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
